import { useEffect, useRef, useState } from 'react'

type Options = NotificationOptions & {
  onClick?: (this: Notification, ev: Event) => void
  onClose?: (this: Notification, ev: Event) => void
  onError?: (this: Notification, ev: Event) => void
  onShow?: (this: Notification, ev: Event) => void
}

export const useNotification = () => {
  const isNotificationCompatible = isNotificationSupported()
  const [isPermissionGranted, setIsPermissionGranted] = useState<boolean>(getInitialNotificationState())

  const notification = useRef<Notification | null>(null)

  const notify = (title: string, options?: Options) => {
    if (isPermissionGranted) {
      notification.current = new Notification(title, {
        ...options,
        icon: `/favicon.ico`,
      })

      if (typeof options?.onClick === 'function') {
        notification.current.onclick = options?.onClick
      }

      if (typeof options?.onClose === 'function') {
        notification.current.onclose = options?.onClose
      }

      if (typeof options?.onError === 'function') {
        notification.current.onerror = options?.onError
      }

      if (typeof options?.onShow === 'function') {
        notification.current.onshow = options?.onShow
      }
    }
  }

  const close = () => {
    notification?.current?.close()
  }

  useEffect(() => {
    if (!isPermissionGranted && isNotificationCompatible) {
      Notification?.requestPermission().then((status) => setIsPermissionGranted(status === 'granted'))
    }
  }, [isPermissionGranted, notification, isNotificationCompatible])

  return {
    notify,
    close,
  }
}

const isNotificationSupported = () =>
  'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window

const getInitialNotificationState = () => {
  const isSupported = isNotificationSupported()
  if (!isSupported) return false
  return Notification?.permission === 'granted'
}
